// Service for Inactivity handling

import {
  FE_CONFIG_KEYS,
  MODAL_CONFIG,
  SESSION_CONFIG,
} from '../../global-config/config';
import { Injectable, NgZone, OnDestroy } from '@angular/core';
import { Subject, fromEvent } from 'rxjs';
import { debounceTime, takeUntil } from 'rxjs/operators';

import { AppConfigService } from '../app-config.service/app-config.service';
import { DOCUMENT_INTERACTION_DEBOUNCE_TIME } from '../../constants/global.constant';
import { InactivityComponent } from '../../components/molecules/inactivity/inactivity.component';
import { LoginService } from '../login-service/login.service';
import { MatDialog } from '@angular/material/dialog';
import { RenderLabelPipe } from '../../pipes/render-label/render-label.pipe';
import { SessionService } from '../session-service/session.service';

@Injectable({
  providedIn: 'root',
})
export class InactivityService implements OnDestroy {
  appTimeoutInSeconds: number;
  timeoutId: number;
  finalTimeoutId: number;
  callback: any = this.resetTimer.bind(this);
  eventsToBind = [
    'click',
    'keypress',
    'scroll',
    'touchstart',
    'mousemove',
    'beforeunload',
  ];
  ngUnsubscribe = new Subject<void>();
  loginService: LoginService | null;

  constructor(
    private readonly dialog: MatDialog,
    private readonly contentPipe: RenderLabelPipe,
    private readonly ngZone: NgZone,
    private readonly sessionService: SessionService,
    private appConfigService: AppConfigService,
  ) {
    // reset on intial load
    this.setCounter();
  }

  /**
   * Inactivity code starts here
   */
  startTimer(): void {
    this.timeoutId = window.setInterval(
      this.doInactive.bind(this),
      SESSION_CONFIG.inactivityCheckTimeout * 1000,
    );
  }

  /**
   * Initialize Timers and bind listeners
   */
  initializeTimers(): void {
    this.ngZone.runOutsideAngular(() => {
      this.eventsToBind.forEach((name: any) => {
        fromEvent(document, name)
          .pipe(
            takeUntil(this.ngUnsubscribe),
            debounceTime(DOCUMENT_INTERACTION_DEBOUNCE_TIME),
          )
          .subscribe(() => {
            this.callback();
          });
      });
      this.startTimer();
    });
  }

  /**
   * Reset timers and remove listeners
   */
  resetTimer(): void {
    this.setCounter();
    window.clearInterval(this.timeoutId);
    this.startTimer();
  }

  /**
   * Removes DOM Listener events
   */
  removeListeners(): void {
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
  }

  /**
   * Angular life cycle
   */
  ngOnDestroy(): void {
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
  }

  /**
   * Sets user to Inactive
   */
  doInactive(): void {
    const lastInactivityCheckTime = this.getCounter();
    this.appTimeoutInSeconds =
      this.appConfigService.appConfig?.[FE_CONFIG_KEYS.InactivityTimeout] ||
      SESSION_CONFIG.inactivityTimeout;
    // show inactive modal 5 minutes before timeout
    if (
      (Date.now() - lastInactivityCheckTime) / 1000 >=
      this.appTimeoutInSeconds - SESSION_CONFIG.inactivityFinalTimeout
    ) {
      window.clearInterval(this.timeoutId);
      this.openInactivityDialog();
      this.removeListeners();
      this.startFinalTimer();
    }
  }

  startFinalTimer() {
    this.finalTimeoutId = window.setTimeout(() => {
      this.loginService?.logout();
    }, SESSION_CONFIG.inactivityFinalTimeout * 1000);
  }

  /**
   * Opens inactivity modal popup
   */
  openInactivityDialog(): void {
    this.ngZone.run(() => {
      const dialogRef = this.dialog.open(InactivityComponent, {
        ...MODAL_CONFIG,
        panelClass: 'no-access-modal-box',
        ariaDescribedBy: this.contentPipe.transform(
          'inactivity_modal_ariaDescribedBy',
        ),
        ariaLabel: this.contentPipe.transform('inactivity_modal_ariaLabel'),
      });

      dialogRef.afterClosed().subscribe((isLogout: boolean) => {
        window.clearTimeout(this.finalTimeoutId);
        if (isLogout) {
          this.loginService?.logout();
        } else {
          this.initializeTimers();
        }
      });
    });
  }

  /**
   * Sets inactivity checked time in session service
   */
  setCounter(): void {
    this.sessionService.set(
      SESSION_CONFIG.inactivityCounterKeyName,
      Date.now().toString(),
    );
  }

  /**
   * Gets inactivity counter from session service
   * @returns counter
   */
  getCounter(): number {
    return Number(
      this.sessionService.get(SESSION_CONFIG.inactivityCounterKeyName),
    );
  }
}
